import { brandApi } from "@/lib/axios";

export type RechargeConnectionStatus =
  | "ACTIVE"
  | "DISCONNECTED"
  | "NEEDS_RECONNECT"
  | "ERROR";

export interface RechargeStatus {
  connected: boolean;
  status: RechargeConnectionStatus | null;
  storeId: string | null;
  myshopifyDomain: string | null;
  /** Non-empty means the Reveshare app in Recharge is missing scopes we need. */
  missingScopes: string[];
  connectedAt: string | null;
  webhooks: {
    registered: number;
    expected: number;
    /** Why registration failed; null when all subscriptions are in place. */
    problem: RechargeWebhookProblem | null;
    /** Recharge's raw reason, for support. Shown only under "Technical details". */
    error: string | null;
  };
}

export type RechargeWebhookProblem = "UNAUTHORIZED" | "REJECTED" | "UNREACHABLE" | "UNKNOWN";

export interface RechargeRetentionAffiliate {
  affiliateId: string;
  firstName: string;
  lastName: string;
  email: string;
  activeSubscriptions: number;
  cancelledSubscriptions: number;
  /** 0–100 */
  retentionRate: number;
  renewals: number;
  paidRenewals: number;
  renewalRevenue: number;
  renewalCommission: number;
}

export interface RechargeRetentionSummary {
  totals: {
    subscriptions: number;
    activeSubscriptions: number;
    cancelledSubscriptions: number;
    retentionRate: number;
    renewals: number;
    renewalRevenue: number;
    renewalCommission: number;
  };
  affiliates: RechargeRetentionAffiliate[];
}

export async function getRechargeAvailability(): Promise<{ available: boolean }> {
  const { data } = await brandApi.get<{ available: boolean }>("/recharge/availability");
  return data;
}

export async function getRechargeStatus(): Promise<RechargeStatus> {
  const { data } = await brandApi.get<RechargeStatus>("/recharge/status");
  return data;
}

/**
 * Recharge's OAuth has no `state`, so the connect flow carries its own proof
 * of origin: the `ticket` issued here is kept in sessionStorage (this tab
 * only) and presented again on claim. See RECHARGE_TICKET_KEY.
 */
export async function getRechargeOauthUrl(): Promise<{ url: string; ticket: string }> {
  const { data } = await brandApi.get<{ url: string; ticket: string }>("/recharge/oauth/url");
  return data;
}

/** sessionStorage key for the install ticket between Connect and the callback. */
export const RECHARGE_TICKET_KEY = "recharge_oauth_ticket";

export type RechargeClaimErrorCode =
  | "TICKET_INVALID"
  | "TICKET_EXPIRED"
  | "TICKET_BRAND_MISMATCH"
  | "GRANT_INVALID"
  | "GRANT_EXPIRED"
  | "STORE_MISMATCH";

/**
 * Finishes the install: the grant the callback put in the URL plus the
 * ticket from getRechargeOauthUrl. 400 with a RechargeClaimErrorCode when
 * they do not line up.
 */
export async function claimRechargeConnection(input: {
  grant: string;
  ticket: string;
}): Promise<RechargeStatus> {
  const { data } = await brandApi.post<RechargeStatus>("/recharge/oauth/claim", input);
  return data;
}

export async function disconnectRecharge(): Promise<{ disconnected: boolean }> {
  const { data } = await brandApi.delete<{ disconnected: boolean }>("/recharge/connection");
  return data;
}

export async function getRechargeRetention(): Promise<RechargeRetentionSummary> {
  const { data } = await brandApi.get<RechargeRetentionSummary>("/recharge/retention");
  return data;
}
